<script setup lang="ts">
import ExcelUpdateBadge from '@/components/badges/ExcelUpdateBadge.vue'
import { ref, computed, onMounted, watch } from 'vue'
import DataTable from 'primevue/datatable'
import Column from 'primevue/column'
import Badge from 'primevue/badge'
import AdminLayout from '@/components/layout/AdminLayout.vue'
import CardComponent from '@/components/cards/CardComponent.vue'
import TextInputDefault from '@/components/forms/TextInputDefault.vue'
import BaseButton from '@/components/Buttons/BaseButton.vue'
import type { Tender } from '@/types/tender'
import HeadingDefault from '@/components/Heading/HeadingOne.vue'
import ModalDefault from '@/components/Modals/ModalDefault.vue'
import AgentAssignmentModal from '../Modals/AgentAssignmentModal.vue'
import { EyeIcon, GlobeIcon, TrashIcon, AgentIcon, RefreshIcon, ExternalIcon } from '@/icons'
// ✅ Import from your enhanced badgeUtils
import {
  getBadgeStatus,
  getWorkflowLabel
} from '@/utils/badgeUtils'
import router from '@/router'
import api from '@/services/api'
import { getAxiosErrorMessage } from '@/utils/errorHandler'
import { notificationService } from '@/services/notificationService'
import EmptyState from '@/components/common/EmptyState.vue'
import TooltipPortal from '@/components/common/TooltipPortal.vue'

const tenders = ref<Tender[]>([])
const searchFilter = ref('')
const loading = ref(false)
const error = ref<string | null>(null)
const showExpired = ref(false)
const expiredTendersCount = ref(0)
const deletingExpired = ref(false)
const showDeleteConfirm = ref(false)

// Add new state variables for individual tender deletion and agent assignment
const isDeleteModalOpen = ref(false)
const tenderToDelete = ref<Tender | null>(null)

const isAgentAssignmentModalOpen = ref(false)
const tenderToAssign = ref<Tender | null>(null)
const agentAssignmentModalRef = ref<InstanceType<typeof AgentAssignmentModal> | null>(null)

// Fetch rejected tenders with centralized API
const fetchRejectedTenders = async () => {
  try {
    loading.value = true
    error.value = null

    // Use query parameter to control whether to include expired tenders
    const url = showExpired.value
      ? '/api/tenders/rejected?includeExpired=true'
      : '/api/tenders/rejected'

    const response = await api.get<Tender[]>(url)

    tenders.value = response.data
    console.log(
      `✅ Loaded ${tenders.value.length} rejected tenders (showExpired: ${showExpired.value})`,
    )
  } catch (err) {
    console.error('Error loading rejected tenders:', err)
    error.value = getAxiosErrorMessage(err)
    notificationService.error(`Failed to load rejected tenders: ${getAxiosErrorMessage(err)}`)
  } finally {
    loading.value = false
  }
}

// Fetch expired rejected count with centralized API
const fetchExpiredCount = async () => {
  try {
    const response = await api.get<Tender[]>('/api/tenders/rejected/expired')
    expiredTendersCount.value = response.data.length
  } catch (err) {
    console.error('Error fetching expired count:', err)
  }
}

// Filtered tenders based on search
const filteredTenders = computed(() => {
  if (!searchFilter.value) return tenders.value

  const searchTerm = searchFilter.value.toLowerCase()
  return tenders.value.filter(
    (tender) =>
      tender.t247Id?.toLowerCase().includes(searchTerm) ||
      tender.referenceNo?.toLowerCase().includes(searchTerm) ||
      tender.tenderBrief?.toLowerCase().includes(searchTerm) ||
      tender.organization?.toLowerCase().includes(searchTerm) ||
      tender.location?.toLowerCase().includes(searchTerm),
  )
})

// Check if tender is expired
const isTenderExpired = (tender: Tender): boolean => {
  if (!tender.deadline) return false
  try {
    const deadline = new Date(tender.deadline)
    const now = new Date()
    return deadline < now
  } catch {
    return false
  }
}

const formatDate = (dateString: string): string => {
  if (!dateString) return 'No deadline'
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateString
  }
}

// ✅ Using getWorkflowLabel for status display
const getStatusLabel = (status: Tender['status']): string => {
  return getWorkflowLabel(status)
}

// ✅ Using getBadgeStatus directly for badge class
const getStatusBadgeClass = (status: Tender['status']): string => {
  return 'p-badge-' + getBadgeStatus(status)
}

const getExpiryStatus = (tender: Tender): { label: string; class: string } => {
  if (!tender.deadline) return { label: 'No deadline', class: 'text-gray-600' }

  if (isTenderExpired(tender)) {
    return { label: 'Expired', class: 'text-red-600 font-medium' }
  }

  try {
    const now = new Date()
    const deadline = new Date(tender.deadline)
    const diffDays = Math.ceil((deadline.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

    if (diffDays < 7) {
      return { label: `${diffDays} days left`, class: 'text-orange-600' }
    }
    return { label: `${diffDays} days left`, class: 'text-green-600' }
  } catch {
    return { label: 'Invalid date', class: 'text-gray-600' }
  }
}

const viewTenderDetails = (tender: Tender) => {
  router.push({
    path: `/tenders/${tender.t247Id}`,
    query: { referrer: 'rejected-tenders' },
  })
}

// Open delete modal for a single tender
// const openDeleteModal = (tender: Tender) => {
//   tenderToDelete.value = tender
//   isDeleteModalOpen.value = true
// }

// Delete a single rejected tender
const deleteSingleTender = async () => {
  if (!tenderToDelete.value) return

  try {
    const tenderId = tenderToDelete.value.t247Id

    await api.delete(`/api/tenders/${tenderId}`)

    // Remove from local state
    tenders.value = tenders.value.filter((t) => t.t247Id !== tenderId)

    // Refresh expired count
    await fetchExpiredCount()

    notificationService.success(`Tender ${tenderId} has been deleted successfully!`)
  } catch (err) {
    console.error('Error deleting tender:', err)
    notificationService.error(`Failed to delete tender: ${getAxiosErrorMessage(err)}`)
  } finally {
    closeDeleteModal()
  }
}

// Close delete modal
const closeDeleteModal = () => {
  isDeleteModalOpen.value = false
  tenderToDelete.value = null
}

// Open agent assignment modal
const openAgentAssignmentModal = (tender: Tender) => {
  tenderToAssign.value = tender
  isAgentAssignmentModalOpen.value = true
}

// Handle agent assignment from rejected tender
const handleAgentAssignment = async (assignmentData: {
  tenderId: string
  agentId: string
  notes: string
  agentName?: string
}) => {
  try {
    console.log('🔄 Assigning agent to rejected tender:', assignmentData)

    let agentName = assignmentData.agentName
    if (!agentName) {
      try {
        const agentResponse = await api.get(`/api/users/agents/${assignmentData.agentId}`)
        if (agentResponse.data && agentResponse.data.name) {
          agentName = agentResponse.data.name
        }
      } catch (agentError) {
        console.warn('Could not fetch agent name:', agentError)
        agentName = 'Unknown Agent'
      }
    }

    const response = await api.put('/api/tenders/assign-agent', {
      tenderId: assignmentData.tenderId,
      agentId: assignmentData.agentId,
      agentName: agentName,
      notes: assignmentData.notes,
      status: 'assigned-to-agent',
      workflowStatus: 'assigned-to-agent',
      assignedAt: new Date().toISOString(),
    })

    console.log('✅ Assignment response:', response.data)

    if (response.data.success) {
      tenders.value = tenders.value.filter((t) => t.t247Id !== assignmentData.tenderId)
      await fetchExpiredCount()

      notificationService.success(
        `Agent "${agentName}" assigned successfully! Tender moved to workflow.`,
      )
      isAgentAssignmentModalOpen.value = false
    } else {
      throw new Error(response.data.error || 'Assignment failed')
    }
  } catch (error) {
    console.error('❌ Assignment error:', error)

    try {
      console.log('🔄 Trying two-step assignment...')

      await api.put('/api/tenders/update-status', {
        tenderIds: [assignmentData.tenderId],
        status: 'assigned-to-agent',
        workflowStatus: 'assigned-to-agent',
        rejectionComment: null,
      })

      await api.put('/api/tenders/assign-agent', {
        tenderId: assignmentData.tenderId,
        agentId: assignmentData.agentId,
        notes: assignmentData.notes,
        status: 'assigned-to-agent',
        workflowStatus: 'assigned-to-agent',
        assignedAt: new Date().toISOString(),
      })

      tenders.value = tenders.value.filter((t) => t.t247Id !== assignmentData.tenderId)
      await fetchExpiredCount()

      notificationService.success('Agent assigned successfully! Tender moved to workflow.')
      isAgentAssignmentModalOpen.value = false
    } catch (fallbackError) {
      console.error('❌ Fallback assignment also failed:', fallbackError)
      notificationService.error(`Failed to assign agent: ${getAxiosErrorMessage(error)}`)
    }
  }
}

// Handle modal assign button click
const handleModalAssign = () => {
  if (agentAssignmentModalRef.value) {
    const success = agentAssignmentModalRef.value.submitForm()
    if (success) {
      isAgentAssignmentModalOpen.value = false
    }
  }
}

// Cancel agent assignment
const handleCancelAssignment = () => {
  isAgentAssignmentModalOpen.value = false
  tenderToAssign.value = null
}

const clearFilters = () => {
  searchFilter.value = ''
}

// Delete expired rejected tenders with centralized API
const deleteExpiredRejectedTenders = async () => {
  try {
    deletingExpired.value = true
    showDeleteConfirm.value = false

    interface DeleteResponse {
      deletedCount: number
      success?: boolean
      message?: string
    }

    const response = await api.delete<DeleteResponse>('/api/tenders/expired-rejected')

    await Promise.all([fetchRejectedTenders(), fetchExpiredCount()])

    const deletedCount = response.data.deletedCount || 0

    if (deletedCount > 0) {
      notificationService.success(`Successfully deleted ${deletedCount} expired rejected tenders.`)
    } else {
      notificationService.info('No expired rejected tenders were found to delete.')
    }
  } catch (err) {
    console.error('Error deleting expired tenders:', err)
    notificationService.error(`Failed to delete expired tenders: ${getAxiosErrorMessage(err)}`)
  } finally {
    deletingExpired.value = false
  }
}

const openDeleteConfirm = () => {
  if (expiredTendersCount.value === 0) {
    notificationService.info('No expired rejected tenders to delete.')
    return
  }
  showDeleteConfirm.value = true
}

const isEligibleForReassignment = (tender: Tender): boolean => {
  const wasAssigned = tender.assignedAgentId || tender.agentAssignedAt
  return !wasAssigned
}

// Watch for showExpired changes
watch(showExpired, () => {
  fetchRejectedTenders()
})

// Tooltip management for description
const tooltipVisible = ref(false);
const tooltipText = ref('');
const tooltipTarget = ref<HTMLElement>(); // Changed from null to undefined
const tooltipPosition = ref<'top' | 'bottom' | 'left' | 'right'>('top');

// Tooltip handlers
const showTooltip = (event: MouseEvent, text: string) => {
  const target = event.currentTarget as HTMLElement;
  tooltipTarget.value = target; // This will be HTMLElement, not null
  tooltipText.value = text;
  tooltipPosition.value = 'top';
  tooltipVisible.value = true;
};

const hideTooltip = () => {
  tooltipVisible.value = false;
  tooltipTarget.value = undefined; // Changed from null to undefined
  tooltipText.value = '';
};

// Lifecycle
onMounted(() => {
  fetchRejectedTenders()
  fetchExpiredCount()
})
</script>

<template>
  <AdminLayout>
    <HeadingDefault heading="Rejected Tenders" />

    <!-- Delete Confirmation Modal (bulk delete) -->
    <div
      v-if="showDeleteConfirm"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <div class="flex items-center mb-4">
          <div class="h-6 w-6 text-red-500 mr-3">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900">Delete Expired Tenders</h3>
        </div>

        <p class="text-gray-600 mb-4">
          Are you sure you want to delete all
          <span class="font-semibold">{{ expiredTendersCount }}</span> expired rejected tenders?
          This action cannot be undone.
        </p>

        <div class="flex justify-end gap-3">
          <BaseButton
            text="Cancel"
            variant="secondary"
            :disabled="deletingExpired"
            @click="showDeleteConfirm = false"
          />
          <BaseButton
            text="Delete"
            variant="danger"
            :loading="deletingExpired"
            :icon="TrashIcon"
            @click="deleteExpiredRejectedTenders"
          />
        </div>
      </div>
    </div>

    <!-- Single Tender Delete Confirmation Modal -->
    <div
      v-if="isDeleteModalOpen && tenderToDelete"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
    >
      <div class="bg-white rounded-lg max-w-md w-full p-6">
        <div class="flex items-center mb-4">
          <div class="h-6 w-6 text-red-500 mr-3">
            <svg fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clip-rule="evenodd"
              />
            </svg>
          </div>
          <h3 class="text-lg font-semibold text-gray-900">Delete Tender</h3>
        </div>

        <p class="text-gray-600 mb-2">Are you sure you want to delete this tender?</p>

        <div class="bg-gray-50 p-3 rounded mb-4">
          <p class="text-sm font-medium">{{ tenderToDelete.referenceNo }}</p>
          <p class="text-xs text-gray-500">{{ tenderToDelete.t247Id }}</p>
          <p class="text-sm mt-1 line-clamp-2">{{ tenderToDelete.tenderBrief }}</p>
        </div>

        <p class="text-red-600 text-sm mb-4">
          ⚠️ This action cannot be undone. The tender will be permanently deleted.
        </p>

        <div class="flex justify-end gap-3">
          <BaseButton text="Cancel" variant="secondary" @click="closeDeleteModal" />
          <BaseButton
            text="Delete"
            variant="danger"
            :icon="TrashIcon"
            @click="deleteSingleTender"
          />
        </div>
      </div>
    </div>

    <!-- Agent Assignment Modal -->
    <ModalDefault
      v-model="isAgentAssignmentModalOpen"
      title="Assign Rejected Tender to Agent"
      width="max-w-lg"
      classBody="my-4"
    >
      <AgentAssignmentModal
        v-if="tenderToAssign"
        ref="agentAssignmentModalRef"
        :tender="tenderToAssign"
        :currentAgentId="tenderToAssign.assignedAgentId"
        @assign="handleAgentAssignment"
        @cancel="handleCancelAssignment"
      />

      <template #footer>
        <div class="flex justify-end gap-3">
          <BaseButton text="Cancel" variant="secondary" @click="handleCancelAssignment" />
          <BaseButton text="Assign Tender" variant="primary" @click="handleModalAssign" />
        </div>
      </template>
    </ModalDefault>

    <!-- Tender List Table -->
    <CardComponent
      className="max-w-full"
      footerClass="justify-end"
      title="Rejected Tender Details"
      :showHeaderBorder="true"
      :showHeaderPaddingBottom="true"
    >
      <template #header>
        <div class="flex flex-col gap-2">
          <div class="flex items-center justify-between gap-2">
            <!-- Search Input -->
            <TextInputDefault
              v-model="searchFilter"
              placeholder="Search rejected tenders..."
              id="searchrejectedtenders"
              name="searchrejectedtenders"
              type="text"
              className=""
              size="xs"
            />

            <!-- Refresh Button -->
            <BaseButton
              size="xs"
              :icon="RefreshIcon"
              @click="fetchRejectedTenders"
              class="ml-2"
              :loading="loading"
            />
            <!-- Clear Filter Button -->
            <BaseButton
              v-if="searchFilter"
              text="Clear Search"
              size="xs"
              variant="ghost"
              @click="clearFilters"
            />
            <BaseButton
              v-if="expiredTendersCount > 0 && !showExpired"
              text="Delete Expired"
              size="xs"
              variant="danger"
              :icon="TrashIcon"
              @click="openDeleteConfirm"
              :disabled="deletingExpired"
              :loading="deletingExpired"
            />
          </div>
        </div>
      </template>

      <template #body>
        <!-- Loading State -->
        <div v-if="loading" class="flex flex-1 gap-2 justify-center text-center py-8">
          <i
            class="animate-spin rounded-full h-5 w-5 border-2 border-current border-t-transparent"
          ></i>
          {{
            showExpired ? 'Loading all rejected tenders...' : 'Loading active rejected tenders...'
          }}
        </div>

        <!-- Error State - Updated to match Dashboard style -->
        <div v-else-if="error" class="mb-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <div class="flex items-center">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm font-medium text-red-800">{{ error }}</p>
              <button
                @click="fetchRejectedTenders"
                class="mt-1 text-sm text-red-700 hover:text-red-600 underline"
              >
                Click here to retry
              </button>
            </div>
          </div>
        </div>

        <!-- No Data State -->
        <EmptyState
          v-else-if="tenders.length === 0"
          :icon="GlobeIcon"
          :title="showExpired ? 'No rejected tenders found' : 'No active rejected tenders'"
          :description="showExpired
                  ? 'There are no rejected tenders in the system.'
                  : 'All rejected tenders have expired or been deleted.'"
        />

        <!-- No Results after Filtering -->
        <EmptyState
          v-else-if="filteredTenders.length === 0"
          :icon="GlobeIcon"
          title="No matching rejected tenders"
          description="No rejected tenders match your search criteria."
          action-text="Clear Filters"
          @action="clearFilters"
        />

        <!-- Tender Table -->
        <template v-else-if="filteredTenders.length > 0">
          <DataTable
            :value="filteredTenders"
            class="w-full"
            sortMode="multiple"
            scrollable
            scrollHeight="400px"
            resizableColumns
            columnResizeMode="fit"
            showGridlines
            :pt="{
              table: { style: 'border-collapse: separate; border-spacing: 0;' },
              thead: { style: 'position: sticky; top: 0; z-index: 20;' },
              tbody: { style: 'position: relative; z-index: 10;' },
            }"
          >
            <!-- Tender ID Column -->
            <Column
              sortable
              frozen
              field="referenceNo"
              header="Reference No"
              headerStyle="position: sticky; left: 48px; z-index: 30;"
              bodyStyle="position: sticky; left: 48px; z-index: 25;"
            >
              <template #body="{ data }">
                <div class="flex flex-col max-w-[100px] text-xs">
                  <a
                    :href="data.tenderBriefLink"
                    target="_blank"
                    class="font-medium hover:underline flex items-center gap-1 break-all whitespace-normal"
                    v-if="data.tenderBriefLink"
                  >
                    {{ data.referenceNo }}
                    <ExternalIcon class="w-3 h-3 text-green-600 flex-shrink-0" />
                  </a>
                  <span v-else class="break-all whitespace-normal">{{ data.referenceNo }}</span>
                  <span class="text-xs text-gray-500 break-all">{{ data.t247Id }}</span>
                  <ExcelUpdateBadge :tender="data" />
                </div>
              </template>
            </Column>

            <!-- Deadline Column -->
            <Column sortable field="deadline" header="Deadline">
              <template #body="{ data }">
                <div class="flex flex-col">
                  <div class="text-xs">
                    {{ formatDate(data.deadline) }}
                  </div>
                  <div class="text-xs" :class="getExpiryStatus(data).class">
                    {{ getExpiryStatus(data).label }}
                  </div>
                  <div v-if="isTenderExpired(data)" class="text-xs text-red-500 font-medium mt-1">
                    ⚠️ Will be auto-deleted
                  </div>
                </div>
              </template>
            </Column>

            <!-- Tender Type -->
            <Column sortable field="sheetType" header="Type">
              <template #body="{ data }">
                <Badge
                  :value="data.sheetType"
                  :class="'p-badge-' + getBadgeStatus(data.sheetType?.toLowerCase())"
                />
              </template>
            </Column>

            <!-- Status Column - Using getWorkflowLabel and getBadgeStatus -->
            <Column sortable field="status" header="Status">
              <template #body="{ data }">
                <Badge
                  :value="getStatusLabel(data.status)"
                  :class="getStatusBadgeClass(data.status)"
                  v-if="isEligibleForReassignment(data)"
                />
                <Badge
                  :value="getStatusLabel(data.workflowStatus)"
                  :class="getStatusBadgeClass(data.status)"
                  v-else
                />
              </template>
            </Column>

            <!-- Rejection Reason Column -->
            <Column sortable field="rejectReason" header="Reason">
              <template #body="{ data }">
                <div class="text-xs">
                  {{ data.rejectionComment || 'Not specified' }}
                </div>
              </template>
            </Column>

            <!-- Tender Brief Column -->
            <Column sortable field="tenderBrief" header="Description">
              <template #body="{ data }">
                <div
                  class="line-clamp-2 text-xs cursor-help"
                  @mouseenter="(event) => showTooltip(event, data.tenderBrief)"
                  @mouseleave="hideTooltip"
                >
                  {{ data.tenderBrief }}
                </div>
              </template>
            </Column>

            <!-- Organization Column -->
            <Column sortable field="organization" header="Organization">
              <template #body="{ data }">
                <div class="text-xs">{{ data.organization }}</div>
              </template>
            </Column>

            <!-- Location Column -->
            <Column sortable field="location" header="Location">
              <template #body="{ data }">
                <div class="flex items-center gap-2 text-xs">
                  {{ data.location }}
                </div>
              </template>
            </Column>

            <!-- Estimated Cost -->
            <Column sortable field="estimatedCost" header="Estimated Cost">
              <template #body="{ data }">
                <div class="font-medium">₹{{ data.estimatedCost?.toLocaleString('en-IN') }}</div>
              </template>
            </Column>

            <!-- Action Column -->
            <Column
              frozen
              field="action"
              header="Actions"
              headerStyle="width: 80px; position: sticky; right: 0; z-index: 30;"
              bodyStyle="position: sticky; right: 0; z-index: 25;"
            >
              <template #body="{ data }">
                <div class="flex gap-2">
                  <!-- View button -->
                  <BaseButton
                    size="xss"
                    :aria-label="`view ${data.t247Id} details`"
                    @click="viewTenderDetails(data)"
                    variant="secondary"
                    :icon="EyeIcon"
                    tooltip="View Tender Details"
                    tooltipPosition="bottom"
                  />
                  <!-- Assign to Agent button -->
                  <BaseButton
                    size="xss"
                    @click="openAgentAssignmentModal(data)"
                    variant="secondary"
                    :icon="AgentIcon"
                    tooltip="Assign to Agent"
                    tooltipPosition="bottom"
                    v-if="isEligibleForReassignment(data)"
                  />
                  <!-- Delete button -->
                  <!-- <BaseButton
                    size="xss"
                    @click="openDeleteModal(data)"
                    variant="danger"
                    :icon="TrashIcon"
                    tooltip="Delete Tender"
                    tooltipPosition="bottom"
                  /> -->
                </div>
              </template>
            </Column>
          </DataTable>
        </template>
      </template>

      <template #footer v-if="!loading && !error && filteredTenders.length > 0">
        <div class="flex justify-between items-center w-full">
          <div class="text-xs text-gray-500">
            Showing {{ filteredTenders.length }} rejected tender(s)
            <span v-if="!showExpired && expiredTendersCount > 0">
              ({{ expiredTendersCount }} expired hidden)
            </span>
          </div>
          <div class="text-xs text-gray-500">
            <span v-if="showExpired"> Showing all rejected tenders including expired </span>
            <span v-else> Showing only active rejected tenders </span>
          </div>
        </div>
      </template>
    </CardComponent>
  </AdminLayout>

  <TooltipPortal
    :visible="tooltipVisible"
    :text="tooltipText"
    :target="tooltipTarget"
    position="auto"
    :max-width="'400px'"
    :multiline="true"
  />
</template>

<style scoped>
/* Toggle switch styles */
.dot {
  transition: all 0.3s ease-in-out;
}
</style>
